import type { MaterialNodeSpec, OperationNodeSpec, GroupSpec, GraphSnapshot } from '../../graph/domain/types';
import type { ModalDescriptor } from '../types';
import { useGraphEditorStore } from '../graphEditorStore';
import { getGroupForOperation } from '../../graph/domain/groupValidation';
import { useShallow } from 'zustand/react/shallow';

export function useSelectedIds(): string[] {
  return useGraphEditorStore((state) => state.selection.cellIds);
}

export function useSelectedGroupIds(): string[] {
  return useGraphEditorStore(
    useShallow((state) => state.selection.cellIds.filter((id) => id in state.graphData.groups)),
  );
}

export function useMaterialNode(id: string | null): MaterialNodeSpec | null {
  return useGraphEditorStore(
    (state) => (id ? state.graphData.materialNodes[id] : null) ?? null,
  );
}

export function useOperationNode(id: string | null): OperationNodeSpec | null {
  return useGraphEditorStore(
    (state) => (id ? state.graphData.operationNodes[id] : null) ?? null,
  );
}

export function useGroup(id: string | null): GroupSpec | null {
  return useGraphEditorStore(
    (state) => (id ? state.graphData.groups[id] : null) ?? null,
  );
}

export function useGroups(): Record<string, GroupSpec> {
  return useGraphEditorStore((state) => state.graphData.groups);
}

export function useSelectedOperations(): OperationNodeSpec[] {
  return useGraphEditorStore((state) =>
    state.selection.cellIds
      .map((id) => state.graphData.operationNodes[id])
      .filter(Boolean),
  );
}

export function useCanGroupSelection(): boolean {
  return useGraphEditorStore((state) => {
    const selectedOps = state.selection.cellIds
      .map((id) => state.graphData.operationNodes[id])
      .filter(Boolean);

    if (selectedOps.length < 1) return false;

    // Check none are already grouped
    for (const op of selectedOps) {
      if (getGroupForOperation(op.id!, state.graphData.groups)) return false;
    }

    // Check contiguity by creationOrder
    const orders = selectedOps
      .map((op) => op.creationOrder ?? -1)
      .filter((o) => o >= 0)
      .sort((a, b) => a - b);

    if (orders.length !== selectedOps.length) return false;
    for (let i = 1; i < orders.length; i++) {
      if (orders[i] !== orders[i - 1] + 1) return false;
    }

    return true;
  });
}

export function useActiveModal(): ModalDescriptor | null {
  return useGraphEditorStore((state) => {
    const stack = state.modals.stack;
    return stack.length > 0 ? stack[stack.length - 1] : null;
  });
}

export function useIsDirty(): boolean {
  return useGraphEditorStore((state) => state.ui.isDirty);
}

export function useGraphSnapshot(): () => GraphSnapshot {
  return useGraphEditorStore((state) => state.toSnapshot);
}

/**
 * Returns all groups that have invalidReason set (i.e., failed load-time validation)
 */
export function useInvalidGroups(): GroupSpec[] {
  return useGraphEditorStore((state) =>
    Object.values(state.graphData.groups).filter((g) => g.invalidReason),
  );
}
